import { CULTURE_QUESTIONS } from '@/config/cultureQuestions';
import type { CultureDimension } from '@/config/cultureQuestions';

export interface CultureScore {
  clan: number;
  adhocracy: number;
  market: number;
  hierarchy: number;
}

export interface CultureResponse {
  department?: string;
  answers: Record<string, number>;
}

const CULTURE_DIMENSIONS: CultureDimension[] = ['clan', 'adhocracy', 'market', 'hierarchy'];

function emptyCultureScore(): CultureScore {
  return {
    clan: 0,
    adhocracy: 0,
    market: 0,
    hierarchy: 0,
  };
}

/**
 * Normaliza un perfil (Likert 1–5 del candidato o puntos 0–100 del empleado) a % de dominancia que suman 100.
 */
export function normalizeCultureScoreTo100(score: CultureScore): CultureScore {
  const total = score.clan + score.adhocracy + score.market + score.hierarchy;
  if (total === 0) return { clan: 25, adhocracy: 25, market: 25, hierarchy: 25 };
  return {
    clan: Number(((score.clan / total) * 100).toFixed(2)),
    adhocracy: Number(((score.adhocracy / total) * 100).toFixed(2)),
    market: Number(((score.market / total) * 100).toFixed(2)),
    hierarchy: Number(((score.hierarchy / total) * 100).toFixed(2)),
  };
}

/**
 * Promedia respuestas por dimensión y normaliza a base 100 (porcentaje de dominancia).
 * Acepta valores Likert 1–5 o puntos ipsativos 0–100 por ítem.
 */
export function computeCultureScores(answers: Record<string, number>): CultureScore {
  const sums: Record<CultureDimension, number> = {
    clan: 0,
    adhocracy: 0,
    market: 0,
    hierarchy: 0,
  };
  const counts: Record<CultureDimension, number> = {
    clan: 0,
    adhocracy: 0,
    market: 0,
    hierarchy: 0,
  };

  for (const question of CULTURE_QUESTIONS) {
    const value = answers[question.id];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) continue;
    sums[question.dimension] += value;
    counts[question.dimension] += 1;
  }

  const rawScore: CultureScore = {
    clan: counts.clan > 0 ? sums.clan / counts.clan : 0,
    adhocracy: counts.adhocracy > 0 ? sums.adhocracy / counts.adhocracy : 0,
    market: counts.market > 0 ? sums.market / counts.market : 0,
    hierarchy: counts.hierarchy > 0 ? sums.hierarchy / counts.hierarchy : 0,
  };

  return normalizeCultureScoreTo100(rawScore);
}

/**
 * Agrega múltiples evaluaciones para obtener el perfil cultural de un grupo (ej. empresa o departamento).
 */
export function computeCultureScoresFromResponses(responses: CultureResponse[]): CultureScore {
  if (responses.length === 0) return emptyCultureScore();

  const aggregate: Record<string, { sum: number; count: number }> = {};

  for (const response of responses) {
    for (const [questionId, value] of Object.entries(response.answers)) {
      if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100) {
        continue;
      }
      const current = aggregate[questionId] ?? { sum: 0, count: 0 };
      current.sum += value;
      current.count += 1;
      aggregate[questionId] = current;
    }
  }

  const averagedAnswers: Record<string, number> = {};
  for (const [questionId, stats] of Object.entries(aggregate)) {
    if (stats.count > 0) {
      averagedAnswers[questionId] = stats.sum / stats.count;
    }
  }

  return computeCultureScores(averagedAnswers);
}

/**
 * Filtra por departamento y calcula el perfil cultural del subconjunto.
 * Si no se envía department, evalúa todas las respuestas.
 */
export function computeCultureScoresByDepartment(
  responses: CultureResponse[],
  department?: string
): CultureScore {
  const normalizedDepartment = department?.trim();
  if (!normalizedDepartment) {
    return computeCultureScoresFromResponses(responses);
  }
  const subset = responses.filter(
    (response) => (response.department?.trim() ?? '') === normalizedDepartment
  );
  return computeCultureScoresFromResponses(subset);
}

/** Extrae respuestas de cultura actual desde un documento `culture_assessments` (ipsativo 0–100 o Likert 1–5 en candidatos no aplica aquí). */
export function extractCultureActualAnswersFromAssessmentDoc(
  data: Record<string, unknown>
): Record<string, number> | null {
  const source = (data.actualAnswers ?? data.answers) as Record<string, unknown> | undefined;
  if (!source || typeof source !== 'object') return null;
  const parsed: Record<string, number> = {};
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100) {
      parsed[key] = value;
    }
  }
  return Object.keys(parsed).length > 0 ? parsed : null;
}

/**
 * Distancia euclidiana inversa transformada a porcentaje (0-100).
 * Perfiles en base 100 por dimensión; distancia máxima teórica en 4D ≈ 141.42.
 */
export function calculateCulturalFit(
  candidateScores: CultureScore,
  companyScores: CultureScore
): number {
  let squaredDistance = 0;
  for (const dimension of CULTURE_DIMENSIONS) {
    const diff = candidateScores[dimension] - companyScores[dimension];
    squaredDistance += diff * diff;
  }

  const distance = Math.sqrt(squaredDistance);
  const maxDistance = 141.42;
  const normalizedDistance = Math.min(distance / maxDistance, 1);
  const fit = (1 - normalizedDistance) * 100;

  return Math.round(fit);
}
